"use client";

import React, { useEffect, useRef } from "react";
import { ShieldCheck, CircleCheck, MessageCircle, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control,
    getValues
  } = useWvcForm();
  const hubspotSentRef = useRef(false);
  useEffect(() => {
    if (isSubmitted && !hubspotSentRef.current) {
      hubspotSentRef.current = true;
      try {
        const values = (typeof getValues === "function" ? getValues() : {}) || {};
        const nome_cognome = values.nome_cognome || "";
        const telefono = values.telefono || "";
        const messaggio = values.messaggio || "";
        const cleanPhone = String(telefono).replace(/\D/g, "");
        const email = values.email || (cleanPhone ? `wa${cleanPhone}@lead.skyclinics.al` : "wa@lead.skyclinics.al");
        fetch("https://api.hsforms.com/submissions/v3/integration/submit/148774927/ccf146d3-182f-4276-bf0d-91a9821aa85f", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            fields: [{
              name: "firstname",
              value: nome_cognome
            }, {
              name: "phone",
              value: telefono
            }, {
              name: "email",
              value: email
            }, {
              name: "message",
              value: messaggio
            }, {
              name: "source_site",
              value: "impiantidentali.co"
            }, {
              name: "lang",
              value: "it"
            }, {
              name: "intent",
              value: "plan"
            }, {
              name: "cta_id",
              value: "idco-home-form"
            }],
            context: {
              pageUri: typeof window !== "undefined" ? window.location.href : "",
              pageName: typeof document !== "undefined" ? document.title : ""
            }
          })
        }).catch(() => {});
      } catch {
        // silently ignore error
      }
    } else if (!isSubmitted) {
      hubspotSentRef.current = false;
    }
  }, [isSubmitted, getValues]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-2xl bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground">Richiesta Inviata!</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-xl border-border/60 hover:bg-accent/20">
          Invia un'altra richiesta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="nome_cognome" rules={{
        required: "Il nome è obbligatorio"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="font-medium text-foreground/90">Nome e Cognome (Obbligatorio)</FormLabel>
              <FormControl>
                <Input placeholder="Mario Rossi" className="h-11 rounded-xl bg-background/50 border-border/60 focus-visible:ring-primary text-foreground" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/80">Inserisci il tuo nome completo</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="telefono" rules={{
        required: "Il numero di telefono è obbligatorio",
        pattern: {
          value: /^[0-9+\-\s()]*$/,
          message: "Inserisci un numero di telefono valido"
        }
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="font-medium text-foreground/90">Numero di Telefono (Obbligatorio)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+39 333 1234567" className="h-11 rounded-xl bg-background/50 border-border/60 focus-visible:ring-primary text-foreground" {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/80">Preferiamo contattarti via telefono</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="messaggio" render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="font-medium text-foreground/90">Messaggio / Domande (Opzionale)</FormLabel>
              <FormControl>
                <textarea className="flex min-h-[110px] w-full rounded-xl border border-border/60 bg-background/50 px-3.5 py-2.5 text-sm text-foreground shadow-xs placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50 resize-y" placeholder="Scrivi qui la tua domanda o le tue esigenze..." {...field} />
              </FormControl>
              <FormDescription className="text-muted-foreground/80">Forniscici ulteriori dettagli per aiutarti meglio</FormDescription>
              <FormMessage />
            </FormItem>} />

        <div className="md:col-span-2 pt-1">
          <p className="text-xs md:text-sm text-muted-foreground">
            La panoramica ti verrà chiesta dopo il primo contatto, solo se serve.
          </p>
        </div>
      </div>

      {submitError && <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" size="lg" className="w-full h-12 rounded-xl text-sm md:text-base font-semibold px-4 shadow-md bg-primary text-primary-foreground hover:bg-primary/90 transition-all" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Invio in corso...
          </> : "Invia Richiesta di Consulenza"}
      </Button>
    </Form>;
}
export default function RichiediUnaConsulenzaGratuita({
  isModal = false
}: {
  isModal?: boolean;
}) {
  return <section id="richiedi-una-consulenza-gratuita" className={`dark bg-background text-foreground px-4 relative overflow-hidden ${isModal ? 'py-4' : 'py-20 md:py-32'}`}>
      {!isModal && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-full pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-2xl blur-3xl opacity-40" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-teal-500/10 rounded-2xl blur-3xl opacity-30" />
        </div>}

      <div className={`container mx-auto relative z-10 ${isModal ? 'max-w-md w-full' : 'max-w-3xl'}`}>
        <div className={`text-center space-y-4 ${isModal ? 'mb-6' : 'mb-12'}`}>
          <h2 className={`${isModal ? 'text-2xl md:text-3xl' : 'text-3xl md:text-5xl'} font-serif font-bold tracking-tight text-foreground`}>
            Richiedi una Consulenza Gratuita
          </h2>
          <p className={`text-muted-foreground mx-auto ${isModal ? 'text-sm' : 'text-lg max-w-2xl'}`}>
            Compila il modulo sottostante per ricevere un'analisi personalizzata e un preventivo senza impegno per il tuo piano di cure dentali in Albania.
          </p>
        </div>

        <Card className="border-border/60 bg-card/80 backdrop-blur-xl shadow-xl rounded-2xl overflow-hidden">
          <CardHeader className={`border-b border-border/40 ${isModal ? 'p-5' : 'p-6 md:p-8 pb-6 md:pb-8'}`}>
            <CardTitle className="text-xl md:text-2xl font-serif flex items-center gap-2.5 text-foreground">
              <ShieldCheck className="size-5 md:size-6 text-primary shrink-0" />
              I tuoi dati sono al sicuro
            </CardTitle>
            <CardDescription className="text-sm md:text-base text-muted-foreground mt-1">
              Trattiamo i tuoi dati con riservatezza. Nessun obbligo dopo la consulenza.
            </CardDescription>
          </CardHeader>
          
          <CardContent className={isModal ? 'p-5 pt-6' : 'p-6 md:p-8 pt-6 md:pt-8'}>
            <FormProvider formId="consultation_form_home" sectionName="RichiediUnaConsulenzaGratuita" defaultValues={{
            nome_cognome: "",
            telefono: "",
            messaggio: ""
          }} submitText="Invia Richiesta" successMessage="Grazie per averci contattato! Un nostro specialista analizzerà la tua richiesta e ti ricontatterà al più presto per fissare la tua consulenza gratuita.">
              <ConsultationFormInner />
            </FormProvider>
          </CardContent>

          <CardFooter className={`flex flex-col items-center border-t border-border/40 bg-muted/20 rounded-b-2xl gap-6 ${isModal ? 'p-5' : 'p-6 md:p-8'}`}>
            <div className="text-center space-y-3 w-full">
              <p className="text-xs sm:text-sm text-muted-foreground font-medium uppercase tracking-wider">
                Oppure per una risposta immediata
              </p>
              <Button variant="outline" size="lg" className="w-full sm:w-auto h-11 rounded-xl border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 hover:text-[#25D366] hover:border-[#25D366]/60 transition-colors" asChild>
                <Link to="https://wa.me/393892866221?text=Buongiorno%2C%20vorrei%20ricevere%20il%20piano%20scritto%20per%20un%20impianto%20dentale.%20%5BIDCO-IT-PLAN-SKY%5D" newTab>
                  <MessageCircle className="mr-2 size-5" />
                  Contatta su WhatsApp
                </Link>
              </Button>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <CircleCheck className="size-4 text-primary shrink-0" />
                Dati riservati
              </span>
              <span className="flex items-center gap-1.5">
                <CircleCheck className="size-4 text-primary shrink-0" />
                Nessun Obbligo
              </span>
              <span className="flex items-center gap-1.5">
                <CircleCheck className="size-4 text-primary shrink-0" />
                Piano scritto entro 24 ore
              </span>
            </div>
          </CardFooter>
        </Card>
      </div>
    </section>;
}